import React, { useState, useEffect } from 'react';

const StudentIDInput = ({ onSubmit, validatingStudentID, error, validationInfo, onSkipToChat, onProceedToSurvey }) => {
  const [studentID, setStudentID] = useState('');
  const [localError, setLocalError] = useState('');
  const [showRetakeConfirm, setShowRetakeConfirm] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  // Watch for validation changes after submission
  useEffect(() => {
    if (justSubmitted && validationInfo?.isRetake) {
      setShowRetakeConfirm(true);
      setJustSubmitted(false);
    }
  }, [validationInfo, justSubmitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    // Basic validation
    if (!studentID.trim()) {
      setLocalError('Please enter your student ID');
      return;
    }

    const idRegex = /^[a-zA-Z0-9]{3,12}$/;
    if (!idRegex.test(studentID)) {
      setLocalError('Student ID must be 3-12 alphanumeric characters');
      return;
    }

    // Call parent submit handler
    setJustSubmitted(true);
    const success = await onSubmit(studentID);

    if (!success) {
      setJustSubmitted(false);
    }
  };

  const handleRetake = () => {
    setShowRetakeConfirm(false);
    // Continue to survey
    if (onProceedToSurvey) {
      onProceedToSurvey();
    }
  };

  const handleGoToChat = () => {
    setShowRetakeConfirm(false);
    if (onSkipToChat) {
      onSkipToChat(studentID, validationInfo?.selectedClusters || []);
    }
  };

  const handleChange = (e) => {
    setStudentID(e.target.value);
    setLocalError('');
  };

  const displayError = error || localError;

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Enter Your Student ID
        </h2>
        <p className="text-gray-600 mb-6">
          Please enter your student ID to begin the survey. This will be used to
          save your responses and generate your career recommendations.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="studentID"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Student ID
            </label>
            <input
              type="text"
              id="studentID"
              value={studentID}
              onChange={handleChange}
              placeholder="Enter student ID"
              className={`
                w-full px-4 py-2 border rounded-lg
                focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                ${displayError ? 'border-red-500' : 'border-gray-300'}
              `}
              disabled={validatingStudentID}
            />
            {displayError && (
              <p className="mt-2 text-sm text-red-600">{displayError}</p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Must be 3-12 alphanumeric characters
            </p>
          </div>

          <button
            type="submit"
            disabled={validatingStudentID}
            className="
              w-full px-6 py-3 rounded-lg font-medium
              bg-blue-600 text-white hover:bg-blue-700
              disabled:bg-blue-400 disabled:cursor-not-allowed
              transition-all flex items-center justify-center space-x-2
            "
          >
            {validatingStudentID ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Validating...</span>
              </>
            ) : (
              <span>Continue to Survey</span>
            )}
          </button>
        </form>

        {/* Retake Confirmation Modal */}
        {showRetakeConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowRetakeConfirm(false)}>
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 text-center">
                Welcome Back!
              </h3>
              <p className="text-gray-600 mb-2 text-center">
                You've already completed this survey.
              </p>
              {validationInfo?.lastSurveyDate && (
                <p className="text-sm text-gray-500 mb-5 text-center">
                  Last completed on{' '}
                  <span className="font-semibold text-gray-700">
                    {new Date(validationInfo.lastSurveyDate).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </p>
              )}
              <p className="text-gray-600 mb-6 text-center">
                What would you like to do?
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleRetake}
                  className="w-full px-6 py-3 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-all flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Retake the Survey</span>
                </button>
                <button
                  onClick={handleGoToChat}
                  className="w-full px-6 py-3 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-all flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Continue to Pathways Advisor</span>
                </button>
                <button
                  onClick={() => setShowRetakeConfirm(false)}
                  className="w-full px-6 py-3 rounded-lg font-medium border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentIDInput;
